import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type BookingsView = 'list' | 'timeline' | 'calendar'
type SettingsTab = 'identity' | 'features' | 'appearance' | 'api-keys' | 'notifications' | 'usage'
type POSTab = 'tables' | 'menu' | 'reports'

interface UIStore {
  bookingsView: BookingsView
  bookingsListFrom: string   // yyyy-MM-dd
  bookingsListTo: string     // yyyy-MM-dd
  bookingsTimelineDate: string  // yyyy-MM-dd
  bookingsCalendarMonth: string // yyyy-MM-dd (first of month)
  settingsTab: SettingsTab

  // POS page state (not persisted — resets on refresh)
  posTab: POSTab
  posSelectedTableDocumentId: string | null
  posOrderDate: string  // yyyy-MM-dd
  posReportsDate: string  // yyyy-MM-dd
  posMenuSelectedCategoryId: string | null
  posFullscreen: boolean

  setBookingsView: (v: BookingsView) => void
  setBookingsListFrom: (d: string) => void
  setBookingsListTo: (d: string) => void
  setBookingsTimelineDate: (d: string) => void
  setBookingsCalendarMonth: (d: string) => void
  setSettingsTab: (tab: SettingsTab) => void

  setPosTab: (tab: POSTab) => void
  setPosSelectedTableDocumentId: (id: string | null) => void
  setPosOrderDate: (d: string) => void
  setPosReportsDate: (d: string) => void
  setPosMenuSelectedCategoryId: (id: string | null) => void
  setPosFullscreen: (fullscreen: boolean) => void
  togglePosFullscreen: () => void
}

const today = new Date()
const pad = (n: number) => String(n).padStart(2, '0')
const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const addDays = (d: Date, n: number) => { const r = new Date(d); r.setDate(r.getDate() + n); return r }
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1)

export const useUIStore = create<UIStore>()(
  persist(
    (set) => ({
      bookingsView: 'list',
      bookingsListFrom: fmt(today),
      bookingsListTo: fmt(addDays(today, 30)),
      bookingsTimelineDate: fmt(today),
      bookingsCalendarMonth: fmt(startOfMonth(today)),
      settingsTab: 'identity',

      posTab: 'tables',
      posSelectedTableDocumentId: null,
      posOrderDate: fmt(today),
      posReportsDate: fmt(today),
      posMenuSelectedCategoryId: null,
      posFullscreen: false,

      setBookingsView: (v) => set({ bookingsView: v }),
      setBookingsListFrom: (d) => set({ bookingsListFrom: d }),
      setBookingsListTo: (d) => set({ bookingsListTo: d }),
      setBookingsTimelineDate: (d) => set({ bookingsTimelineDate: d }),
      setBookingsCalendarMonth: (d) => set({ bookingsCalendarMonth: d }),
      setSettingsTab: (tab) => set({ settingsTab: tab }),

      setPosTab: (tab) => set({ posTab: tab }),
      setPosSelectedTableDocumentId: (id) => set({ posSelectedTableDocumentId: id }),
      setPosOrderDate: (d) => set({ posOrderDate: d }),
      setPosReportsDate: (d) => set({ posReportsDate: d }),
      setPosMenuSelectedCategoryId: (id) => set({ posMenuSelectedCategoryId: id }),
      setPosFullscreen: (fullscreen) => set({ posFullscreen: fullscreen }),
      togglePosFullscreen: () => set((state) => ({ posFullscreen: !state.posFullscreen })),
    }),
    {
      name: 'ui-store',
      partialize: (state) => ({
        bookingsView: state.bookingsView,
        bookingsListFrom: state.bookingsListFrom,
        bookingsListTo: state.bookingsListTo,
        bookingsTimelineDate: state.bookingsTimelineDate,
        bookingsCalendarMonth: state.bookingsCalendarMonth,
        settingsTab: state.settingsTab,
        posTab: state.posTab,
        posSelectedTableDocumentId: state.posSelectedTableDocumentId,
        posOrderDate: state.posOrderDate,
        posReportsDate: state.posReportsDate,
        posMenuSelectedCategoryId: state.posMenuSelectedCategoryId,
      }),
    },
  ),
)
